import { Component, createRef } from 'preact';
import { cn } from '../lib/utils';
import { TEXT_PRIMARY, TEXT_MUTED, ACCENT_GOLD } from '../lib/medievalTheme';

// ── Matchup backdrop ───────────────────────────────────────────
//
// The fighting-game splash both seats' Avatar art is cut into: two halves
// split by the same lightning crease FriendMatchHero uses. The winning half
// stays lit with a gold rim, the losing half is drained of colour and rimmed
// red. Shared by every surface that stands for one finished match: the match
// result screen, the replay card in a DM, and the Past Matches tiles.
//
// `headings` draws the big Won / Lost banner plus each seat's name, which
// only the full-panel result screen has room for. `animated` runs the
// entrance choreography (halves sliding in, the loser draining as the
// verdict is called); a grid of tiles turns it off and lands on the end
// state, since a dozen splashes animating at once is noise, not drama.

// The crease, as (x-offset from centre, y) pairs. Both axes are percentages
// of the element, which is exactly why the shape cannot be a constant: a
// clip-path percentage resolves against the box's own width and height, so
// the same numbers that draw a shallow lightning bolt down a 16:9 panel draw
// a fat sideways arrow across a short, wide card. `creaseAmplitude` below
// rescales the x-offsets by the box's aspect so the zigzag keeps the angle it
// has on the result screen, whatever it is drawn on.
const CREASE_POINTS = [[6, 0], [-4, 22], [6, 44], [-8, 60], [4, 82], [-6, 100]];
const REF_ASPECT = 1600 / 900;

const DRAINED = 'grayscale(0.9) brightness(0.55)';

/**
 * How far the crease swings off centre, as a multiple of its width on the
 * result panel. Holds `dx_px / dy_px` constant, so the bolt leans the same
 * amount everywhere. Clamped: past these the shape stops reading as a bolt.
 */
export function creaseAmplitude(width, height) {
  if (!width || !height) return 1;
  return Math.min(1.5, Math.max(0.25, REF_ASPECT / (width / height)));
}

const creaseX = (dev, k) => 50 + dev * k;

function leftClip(k) {
  const [head, ...rest] = CREASE_POINTS;
  const pts = rest.map(([dev, y]) => `${creaseX(dev, k)}% ${y}%`).join(', ');
  return `polygon(0 0, ${creaseX(head[0], k)}% 0, ${pts}, 0 100%)`;
}

function rightClip(k) {
  const [head, ...rest] = CREASE_POINTS;
  const pts = [...rest].reverse().map(([dev, y]) => `${creaseX(dev, k)}% ${y}%`).join(', ');
  return `polygon(${creaseX(head[0], k)}% 0, 100% 0, 100% 100%, ${pts})`;
}

const creasePoints = (k) => CREASE_POINTS.map(([dev, y]) => `${creaseX(dev, k)},${y}`).join(' ');

function MatchupHalf({ side, name, url, won, neutral, headings, animated, k }) {
  const left = side === 'left';
  const clip = left ? leftClip(k) : rightClip(k);
  const lost = !won && !neutral;
  return (
    <div
      className={cn(
        'absolute inset-0 matchup-half',
        left ? 'matchup-half-left' : 'matchup-half-right',
        animated && (left ? 'match-result-half-left' : 'match-result-half-right'),
      )}
      style={{ clipPath: clip, WebkitClipPath: clip }}
    >
      {/* The drain waits for the Won / Lost headings: both halves land lit,
          then the loser's colour bleeds out as the verdict is called. */}
      <div
        className={cn('absolute inset-0', lost && animated && 'match-result-half-drain')}
        style={lost && !animated ? { filter: DRAINED } : undefined}
      >
        {url ? (
          <img
            src={url}
            alt=""
            draggable={false}
            className="max-w-none"
            style={{
              position: 'absolute',
              left: left ? '26%' : '74%',
              top: '50%',
              height: '190%',
              width: 'auto',
              transform: 'translate(-50%, -44%)',
            }}
            onError={(e) => {
              // Hiding a broken avatar silently cost an afternoon of guessing
              // why solo results rendered two empty halves. Say what failed.
              console.warn('[MatchupBackdrop] avatar image failed to load:', url);
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : null}
      </div>
      {/* Side-specific colour wash + rim glow */}
      <div
        className={cn(
          'absolute inset-0',
          animated && 'match-result-rim',
          animated && (won && !neutral ? 'match-result-rim-won' : lost ? 'match-result-rim-lost' : null),
        )}
        style={{
          background: won && !neutral
            ? `linear-gradient(${left ? 135 : 225}deg, rgba(212,168,67,0.22) 0%, rgba(60,40,10,0.1) 50%, transparent 100%)`
            : lost
              ? `linear-gradient(${left ? 135 : 225}deg, rgba(150,30,30,0.35) 0%, rgba(40,10,10,0.25) 60%, transparent 100%)`
              : 'none',
        }}
      />
      {headings ? (
        <>
          {/* Side heading */}
          <div
            className={cn('absolute top-4 arena-heading font-bold', left ? 'left-5' : 'right-5', won && !neutral ? 'match-result-side-won' : lost ? 'match-result-side-lost' : null)}
            style={{
              fontSize: '1.75cqw',
              lineHeight: 1,
              color: won && !neutral ? '#f5d782' : lost ? '#e37070' : '#b9c2cf',
            }}
          >
            {neutral ? '' : won ? 'Won' : 'Lost'}
          </div>
          <div
            className={cn('absolute top-[3.25cqw] text-[0.8125cqw] font-semibold truncate max-w-[42%]', left ? 'left-5 text-left' : 'right-5 text-right')}
            style={{ color: won && !neutral ? TEXT_PRIMARY : TEXT_MUTED, textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}
          >
            {name}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default class MatchupBackdrop extends Component {
  state = { k: 1 };

  // The crease angle depends on the box it is drawn in, and nothing else on
  // the page knows that box: a Past Matches tile, a chat bubble and the
  // result panel are all different shapes. So measure it.
  componentDidMount() {
    const el = this.ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const k = creaseAmplitude(width, height);
      if (Math.abs(k - this.state.k) > 0.01) this.setState({ k });
    });
    this.ro.observe(el);
  }

  componentWillUnmount() {
    this.ro?.disconnect();
  }

  ref = createRef();

  render() {
    const { matchup, won, neutral, headings = true, animated = true, scrim = 1, edgeScrim = scrim, creaseStroke = 1 } = this.props;
    const { k } = this.state;
    return (
      <div ref={this.ref} className="matchup-backdrop absolute inset-0 overflow-hidden pointer-events-none" style={{ background: '#0b0806' }}>
        {matchup ? (
          <>
            <MatchupHalf side="left" name={matchup.localName} url={matchup.localAvatarUrl} won={won} neutral={neutral} headings={headings} animated={animated} k={k} />
            <MatchupHalf side="right" name={matchup.remoteName} url={matchup.remoteAvatarUrl} won={!won} neutral={neutral} headings={headings} animated={animated} k={k} />
            <svg className={cn('absolute inset-0 w-full h-full matchup-crease', animated && 'match-result-crease')} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
              {/* Non-scaling stroke, so the same width reads far heavier on a
                  small card than on the result panel. `creaseStroke` trims it
                  back to the same visual weight. */}
              <polyline points={creasePoints(k)} fill="none" stroke="rgba(255, 240, 180, 0.45)" strokeWidth={3 * creaseStroke} strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
              <polyline points={creasePoints(k)} fill="none" stroke={ACCENT_GOLD} strokeWidth={1.2 * creaseStroke} strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
            </svg>
          </>
        ) : null}
        {/* Scrim: keeps whatever sits on top legible over the art. Heavier
            toward the centre column and the bottom where the content sits.
            `scrim` thins the centre wash; `edgeScrim` the top and bottom
            bands the title and footer are read against. A small card wants
            less of the first and all of the second. */}
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(ellipse 46% 80% at 50% 55%, rgba(6,4,2,${0.9 * scrim}) 0%, rgba(6,4,2,${0.72 * scrim}) 55%, rgba(6,4,2,${0.15 * scrim}) 100%)` }}
        />
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(180deg, rgba(6,4,2,${0.15 * edgeScrim}) 0%, rgba(6,4,2,0) 25%, rgba(6,4,2,${0.75 * edgeScrim}) 70%, rgba(6,4,2,${0.95 * edgeScrim}) 100%)` }}
        />
      </div>
    );
  }
}
